'use client';

import React from 'react';
import Link from 'next/link';
import { useMessages, useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';

import { useLocale } from "@/app/components/LocaleProvider";
import { getPathWithLocale } from "@/lib/i18n";

interface BreadcrumbItem {
  label: string;
  href: string;
}

type MessageTree = Record<string, Record<string, string>>;

const Breadcrumb: React.FC = () => {
  const pathname = usePathname();
  const locale = useLocale();
  const t = useTranslations('Breadcrumb');
  const messages = useMessages() as MessageTree;
  const breadcrumbMessages = messages.Breadcrumb ?? {};

  const resolveBreadcrumbText = (key: string, fallback?: string) => {
    if (typeof breadcrumbMessages[key] === 'string') {
      return t(key as never);
    }

    return fallback ?? key;
  };

  if (pathname === '/' || pathname === '/ru' || pathname === '/en') {
    return null;
  }

  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const segments = pathname.split('/').filter((segment) => segment);
    const breadcrumbs: BreadcrumbItem[] = [];

    breadcrumbs.push({
      label: resolveBreadcrumbText('home'),
      href: getPathWithLocale('/', locale)
    });

    let currentPath = '';
    segments.forEach((segment, index) => {
      if (index === 0 && (segment === 'ru' || segment === 'en')) {
        return;
      }

      currentPath += `/${segment}`;
      const isLast = index === segments.length - 1;
      let label = segment;

      const isDynamicSegment =
        index === segments.length - 1 &&
        (
          (segments[0] === 'consulting' && ['audit', 'business-analysis', 'architecture', 'poc'].includes(segment)) ||
          (segments[0] === 'development' && segments.length === 2) ||
          (segments[0] === 'support' && segments.length === 2) ||
          (segments[0] === 'technologies' && segments.length === 2) ||
          (segments[0] === 'work' && segments.length === 2) ||
          (segments[0] === 'industries' && segments.length === 2) ||
          (segments[0] === 'bookings' && segments.length === 2)
        );

      if (isDynamicSegment) {
        if (segments[0] === 'consulting') {
          label = resolveBreadcrumbText('consultingService');
        } else if (segments[0] === 'development') {
          label = resolveBreadcrumbText('developmentService');
        } else if (segments[0] === 'support') {
          label = resolveBreadcrumbText('supportService');
        } else if (segments[0] === 'technologies') {
          label = resolveBreadcrumbText('technology');
        } else if (segments[0] === 'work') {
          label = resolveBreadcrumbText('project');
        } else if (segments[0] === 'industries') {
          const industryTranslation = resolveBreadcrumbText(segment);
          label = industryTranslation !== segment ? industryTranslation : resolveBreadcrumbText('industry');
        } else if (segments[0] === 'bookings') {
          label = resolveBreadcrumbText('booking');
        } else {
          label = resolveBreadcrumbText('detailPage');
        }
      } else {
        label = segment.replace(/-/g, ' ');
        label = label.charAt(0).toUpperCase() + label.slice(1);

        const translated = resolveBreadcrumbText(segment);
        if (translated !== segment) {
          label = translated;
        }
      }

      breadcrumbs.push({
        label,
        href: isLast ? '' : getPathWithLocale(currentPath, locale)
      });
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  return (
    <nav
      aria-label="Breadcrumb"
      className="container mx-auto max-w-7xl px-4 pb-1 pt-[calc(3.5rem+env(safe-area-inset-top)+0.5rem)]"
    >
      <ol className="flex items-center space-x-2 text-xs text-white/45">
        {breadcrumbs.map((item, index) => (
          <React.Fragment key={index}>
            {index > 0 && <span className="text-gray-500">/</span>}
            {item.href ? (
              <Link
                href={item.href}
                className="transition-colors duration-200 hover:text-white/80"
              >
                {item.label}
              </Link>
            ) : (
              <span className="font-medium text-white/75">{item.label}</span>
            )}
          </React.Fragment>
        ))}
      </ol>
    </nav>
  );
};

export default Breadcrumb;
